import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, GraduationCap, MessageCircle, Sparkles } from "lucide-react";
import Seo, { breadcrumbLd } from "@/components/seo/Seo";
import FaqSection from "@/components/common/FaqSection";
import RelatedCaseStudies from "@/components/common/RelatedCaseStudies";
import { trackEvent, trackViewOnce } from "@/lib/analytics";

const openEstimateWizard = (source) => {
  // NB: don't push project_estimate_started here — the global wizard fires it once.
  window.dispatchEvent(new CustomEvent("open-estimate-wizard", { detail: { source } }));
};

const CAPABILITIES = [
  { title: "Admissions CRMs", desc: "Enquiry → application → enrolment funnels, with WhatsApp, phone and web capture, lead scoring and automation." },
  { title: "Student portals", desc: "Self-service portals for applicants and enrolled students — documents, fees, transcripts, communication." },
  { title: "LMS & online teaching", desc: "Learning platforms for courses, lessons, assessments, live classes and progress tracking." },
  { title: "Campus / Education ERPs", desc: "Finance, HR, academics, hostel, transport, library and reporting — one platform for the whole institution." },
  { title: "AI-assisted interviews & counselling", desc: "AI voice agents for pre-admission interviews, student counselling and visa-interview practice." },
  { title: "Document processing automation", desc: "OCR + LLMs for transcripts, passports, KYC, financial documents and admission paperwork." },
  { title: "Mobile applications", desc: "Student, parent and staff apps for iOS + Android with offline access and push notifications." },
  { title: "Application workflow automation", desc: "Multi-stage application flows with document requests, approvals, payments and status tracking." },
  { title: "Communication automation", desc: "WhatsApp, email and SMS journeys triggered by application state, deadlines and enrolment events." },
];

const FAQS = [
  { q: "Which types of education organisations does CloudLab work with?", a: "Universities, colleges, K-12 schools, education consultancies, admissions agents, EdTech start-ups and online-teaching platforms — from single-campus to multi-campus and international groups." },
  { q: "Do you build custom LMS or can you integrate existing ones?", a: "Both. Where an off-the-shelf LMS (Canvas, Moodle, Google Classroom) fits, we integrate. Where the workflow is competitively different, we build custom." },
  { q: "Can you migrate data from our existing student system?", a: "Yes — we've migrated from Excel spreadsheets, custom PHP systems, legacy campus ERPs and spreadsheets-plus-email operations. Full audit, dedupe and rollback plan every time." },
  { q: "Do you support multi-campus operations?", a: "Yes — multi-campus, multi-country and multi-department roll-outs are our default architecture for education ERPs and CRMs." },
  { q: "Can AI help with admissions?", a: "Yes — AI voice agents can qualify pre-admission enquiries, book counselling calls, handle repetitive FAQs, and even run practice visa interviews. All grounded in your data with guardrails." },
  { q: "How long does an admissions CRM project take?", a: "Focused admissions CRMs (single-department) typically go live in 10–14 weeks. Multi-department, multi-campus roll-outs run 4–6 months delivered incrementally." },
  { q: "Who owns the code and data?", a: "You do — fully. Source code, deployment credentials, database and IP transfer are contractual defaults." },
  { q: "Do you support ongoing operations after launch?", a: "Yes — SLA-backed support plans with defined incident response, patch management and monitoring. Most education clients stay on multi-year support arrangements." },
];

export default function EducationIndustryPage() {
  useEffect(() => {
    trackViewOnce("industry_page_view", { industry: "education" });
  }, []);

  return (
    <div data-testid="industry-education">
      <Seo
        title="Education & EdTech Software Development | Admissions CRM, LMS & AI | CloudLab"
        description="Education software development company. CloudLab builds admissions CRMs, student portals, LMS platforms, campus ERPs and AI-assisted admissions for universities, colleges and EdTech companies."
        keywords="education software development, EdTech software development company, education CRM development, student recruitment software, LMS development, AI for education, student portal development, admissions CRM, campus ERP"
        canonical="https://www.cloudlabweb.com/industries/education"
        jsonLd={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Industries", path: "/industries" },
          { name: "Education", path: "/industries/education" },
        ])}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(1100px 500px at 15% 0%, hsl(var(--primary) / 0.12) 0%, transparent 60%), radial-gradient(900px 400px at 100% 20%, hsl(var(--secondary) / 0.10) 0%, transparent 60%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 pt-16 md:pt-24 pb-10 md:pb-16">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 border border-border/60 bg-background/70 backdrop-blur-sm">
            <GraduationCap className="w-3.5 h-3.5 text-primary" />
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-[0.22em]">Education & EdTech · Admissions · LMS · AI</span>
          </div>
          <h1
            className="mt-6 font-heading font-black tracking-tight leading-[0.98] text-5xl md:text-6xl lg:text-[5.25rem] max-w-5xl"
            data-testid="industry-education-hero-title"
          >
            Education & EdTech Software{" "}
            <span className="italic underline decoration-[3px] underline-offset-[6px] text-primary decoration-primary">
              Development.
            </span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed">
            CloudLab Technologies has spent over a decade building software for universities, colleges,
            education consultancies and EdTech companies — admissions CRMs, student portals, LMS platforms,
            campus ERPs and AI-assisted admissions.
          </p>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl leading-relaxed">
            From single-campus institutions to multi-country education groups — we build the software that
            runs enrolment, teaching and operations.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={() => openEstimateWizard("industry_education_hero")}
              className="group rounded-full pl-6 pr-2 py-2 inline-flex items-center gap-3 text-sm font-medium bg-primary text-primary-foreground shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)]"
              data-testid="industry-education-cta-primary"
            >
              <span>Get a Free Project Estimate</span>
              <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                <ArrowRight className="w-4 h-4" />
              </span>
            </button>
            <Link
              to="/contact"
              className="rounded-full px-6 py-3 border border-border text-sm font-medium hover:bg-foreground/5 transition-colors"
              data-testid="industry-education-cta-secondary"
            >
              Book a 15-Minute Consultation
            </Link>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="py-14 md:py-20 bg-muted/30" data-testid="industry-education-capabilities">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">What we build for education</div>
            <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl tracking-tight leading-[1.05]">
              Software across the entire student journey.
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPABILITIES.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
                className="rounded-2xl border border-border bg-card p-6"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />
                </div>
                <div className="mt-4 font-heading font-bold text-base leading-tight">{c.title}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED CASE STUDIES (automatically filtered to industry=education) */}
      <RelatedCaseStudies
        industry="education"
        kicker="Case studies · Education"
        title="Verified education projects"
      />

      {/* INTERNAL LINKS TO RELATED SERVICES */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Related services</div>
          <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl tracking-tight leading-[1.1]">Explore the services behind education software.</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { slug: "crm-development",   name: "CRM Development",    tagline: "Admissions & student CRMs." },
              { slug: "erp-development",   name: "ERP Development",    tagline: "Campus & multi-campus ERPs." },
              { slug: "ai-development",   name: "AI Development",     tagline: "AI copilots & voice agents for admissions." },
              { slug: "mobile-app-development", name: "Mobile Apps",   tagline: "Student, parent & staff apps." },
            ].map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="group rounded-2xl border border-border bg-card p-5 hover:border-primary/50 transition-colors">
                <div className="font-heading font-bold text-base leading-tight group-hover:text-primary transition-colors">{s.name}</div>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.tagline}</p>
                <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary">
                  Read more <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection kicker="Education · FAQ" title="Frequently asked questions" items={FAQS} testidRoot="industry-education-faq" />

      {/* FOOTER CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading font-black tracking-tight text-4xl md:text-5xl leading-[1.05]">
            Building software for an education organisation?
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Share the shape of the project and we&apos;ll come back with a scope, timeline and price within one business day.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => openEstimateWizard("industry_education_footer")}
              className="group rounded-full pl-6 pr-2 py-2 inline-flex items-center gap-3 text-sm font-medium bg-primary text-primary-foreground shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)]"
              data-testid="industry-education-footer-cta-primary"
            >
              <span>Get a Free Project Estimate</span>
              <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                <ArrowRight className="w-4 h-4" />
              </span>
            </button>
            <Link
              to="/contact"
              className="rounded-full px-6 py-3 border border-border text-sm font-medium hover:bg-foreground/5 transition-colors inline-flex items-center gap-2"
              data-testid="industry-education-footer-cta-secondary"
            >
              <MessageCircle className="w-3.5 h-3.5" /> Book a 15-Minute Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
